import {
  ArrowNarrowRightIcon,
  PauseIcon,
  PlayIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';

import styles from './SoundEffects.module.css';

import { SOUND_EFFECT_EXAMPLES } from '@/constants/soundEffectExamples';

export default function SoundExamples({
  onUsePrompt,
}: {
  onUsePrompt?: (prompt: string) => void;
}) {
  const { t } = useLocale('sound-effects');
  const [selected, setSelected] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState<number>(
    SOUND_EFFECT_EXAMPLES[0].duration,
  );
  const audioRef = useRef<HTMLAudioElement>(null);
  const playRequest = useRef(0);
  const example = SOUND_EFFECT_EXAMPLES[selected];

  useEffect(() => {
    const audio = audioRef.current;
    const request = playRequest;
    return () => {
      request.current++;
      audio?.pause();
    };
  }, []);

  async function play(index: number) {
    const audio = audioRef.current;
    if (!audio) return;
    const request = ++playRequest.current;
    if (selected === index && (playing || loading)) {
      audio.pause();
      setPlaying(false);
      setLoading(false);
      return;
    }
    audio.pause();
    setError(false);
    setLoading(true);
    if (index !== selected || !audio.getAttribute('src')) {
      audio.src = SOUND_EFFECT_EXAMPLES[index].url;
      setSelected(index);
      setTime(0);
      setDuration(SOUND_EFFECT_EXAMPLES[index].duration);
    }
    try {
      await audio.play();
    } catch {
      if (playRequest.current === request) {
        setError(true);
        setPlaying(false);
      }
    } finally {
      if (playRequest.current === request) setLoading(false);
    }
  }

  return (
    <div className={styles.listeningRoom}>
      <div className={styles.deckHeader}>
        <span className={styles.eyebrow}>{t('The listening room')}</span>
        <span className={styles.liveBadge}>
          <span /> Seed Audio 1.0
        </span>
      </div>
      <div className={styles.deckTitle}>
        <span>{t(example.category)}</span>
        <h2>{t(example.name)}</h2>
      </div>
      <svg className={styles.waveform} viewBox='0 0 640 112' aria-hidden='true'>
        {example.peaks.map((peak, i) => {
          const height = Math.max(3, peak);
          return (
            <rect
              key={i}
              x={i * 8}
              y={(112 - height) / 2}
              width='4'
              height={height}
              rx='2'
              fill={
                i / example.peaks.length < time / duration
                  ? 'var(--wave-played, #c4b5fd)'
                  : 'var(--wave-unplayed, #696478)'
              }
            />
          );
        })}
      </svg>
      <div className={styles.transport} dir='ltr'>
        <button
          type='button'
          onClick={() => play(selected)}
          aria-label={playing ? t('Pause sample') : t('Play sample')}
          className={styles.mainPlay}
        >
          {playing ? <PauseIcon /> : <PlayIcon />}
        </button>
        <label className={styles.seek}>
          <span className='sr-only'>{t('Seek audio')}</span>
          <input
            type='range'
            min='0'
            max={duration}
            step='0.1'
            value={Math.min(time, duration)}
            onChange={(e) => {
              if (audioRef.current && audioRef.current.readyState >= 1) {
                audioRef.current.currentTime = Number(e.target.value);
                setTime(Number(e.target.value));
              }
            }}
          />
        </label>
        <span className={styles.time}>
          {Math.floor(time)} / {Math.round(duration)} {t('sec')}
        </span>
      </div>
      <div className={styles.demoList}>
        {SOUND_EFFECT_EXAMPLES.map((sound, index) => (
          <button
            key={sound.id}
            type='button'
            onClick={() => play(index)}
            className={styles.demoRow}
            aria-pressed={selected === index && playing}
          >
            <span className={styles.trackNumber}>
              {selected === index && playing ? (
                <PauseIcon />
              ) : (
                String(index + 1).padStart(2, '0')
              )}
            </span>
            <span className={styles.trackName}>
              {t(sound.name)}
              <small>{t(sound.category)}</small>
            </span>
            <span className={styles.trackDuration}>{sound.duration}s</span>
            <PlayIcon className={styles.rowPlay} />
          </button>
        ))}
      </div>
      <p className={styles.samplePrompt}>{t(example.prompt)}</p>
      <div className={styles.deckFooter}>
        <span>{t('Real generations. Press play.')}</span>
        {onUsePrompt ? (
          <button type='button' onClick={() => onUsePrompt(t(example.prompt))}>
            {t('Use this prompt')} <ArrowNarrowRightIcon />
          </button>
        ) : null}
      </div>
      <p role='status' className={styles.playerStatus}>
        {error
          ? t('Could not play this sample. Please try again.')
          : loading
            ? t('Loading audio…')
            : ''}
      </p>
      <audio
        ref={audioRef}
        preload='none'
        onPlaying={() => {
          setPlaying(true);
          setLoading(false);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setTime(0);
        }}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          if (Number.isFinite(e.currentTarget.duration))
            setDuration(e.currentTarget.duration);
        }}
      />
    </div>
  );
}
